import type { FileRecord } from "@cohesive/api-client";
import { isPageFileRecord, WEBPAGE_ORIGIN_TYPE } from "@cohesive/ui-components";

// Landscape tile (2:1 thumbnail plus the ~46px embedded footer) for the
// canvas things that are not pages: html BLOCKS that arrive without authored
// dimensions, generic entities with a cover image, webpage placeholders and
// the URL-ingest error card. File records no longer default to this — see
// DOC_PORTRAIT_* below.
//
// Image/video tiles override this with their natural aspect via
// `fitImageNodeSize` (and are inherently off-grid — see the edge-alignment
// analysis).
export const DEFAULT_FILE_NODE_WIDTH = 320;
export const DEFAULT_FILE_NODE_HEIGHT = 192;

// Every page-shaped file record (anything that is not image/* or video/* —
// markdown, html documents, pdf, txt, office documents, spreadsheets,
// archives, audio, unknown — except a web-page capture, which is a
// screenshot at its own aspect) presents as a portrait page — US Letter
// proportions (8.5:11 is exactly 17:22). On the 16px lattice at the standard
// 320 card width that is 320×416 (20×26 steps, 0.45% off exact; exact would
// be 272×352, which would break the canvas-wide 320 column). The card's 48px
// filename footer leaves a 320×368 well. Every node-creation path (drop,
// upload, workspace copy/reference, paste, agent placement, URL ingest, "New
// Doc") shares these via defaultFileRecordNodeBox so a page lands portrait
// regardless of route.
export const DOC_PORTRAIT_NODE_WIDTH = 320;
export const DOC_PORTRAIT_NODE_HEIGHT = 416;

// The page predicate lives next to the poster renderer in ui-components so
// the node box and the poster crop can never disagree.
export { isPageFileRecord };

/** Whether a file record's node takes a box fitted to natural pixel
 *  dimensions when it has them: photos and videos, and web-page captures —
 *  whose poster is the page's screenshot at the source aspect. */
function fitsNaturalAspect(mime: string, originType: unknown): boolean {
  return (
    mime.startsWith("image/") ||
    mime.startsWith("video/") ||
    originType === WEBPAGE_ORIGIN_TYPE
  );
}

/** Default canvas box for a file record node: the portrait page for every
 *  page-shaped record, the landscape tile only for media (or a web-page
 *  capture) that is missing its dimensions — with dimensions, the callers
 *  fit the natural aspect first and never reach here. Callers that know the
 *  node is an HTML BLOCK must not use this — blocks carry authored
 *  dimensions. */
export function defaultFileRecordNodeBox(
  contentType?: string | null,
  originType?: string | null,
): {
  width: number;
  height: number;
} {
  return isPageFileRecord({
    content_type: contentType,
    origin_type: originType,
  })
    ? { width: DOC_PORTRAIT_NODE_WIDTH, height: DOC_PORTRAIT_NODE_HEIGHT }
    : { width: DEFAULT_FILE_NODE_WIDTH, height: DEFAULT_FILE_NODE_HEIGHT };
}

/**
 * Minimal file_record entity shape per `base-file-record.json`, plus
 * a few optional hints we seed at upload time so the canvas can render
 * the correct aspect/icon before hydration completes:
 *
 * - `content_type` is required by `getEntityMediaDisplayBox` to decide
 *   whether the entity is image/video (and therefore should keep its
 *   computed aspect instead of falling back to `aspect-square`).
 * - `width`/`height` are read by the same helper to compute the fitted
 *   media box without waiting on the server's `metadata.width/height`
 *   (which only appears post-processing).
 *
 * These optional fields are intentionally compatible with the wire
 * spread done by `useHydrateFileRecordEntity` — the server's `FileRecord`
 * doesn't carry top-level `width`/`height`, so our pre-seeded values
 * survive hydration unchanged.
 */
export interface FileRecordCanvasEntity {
  type: "file_record";
  base: "file_record";
  platform: "cohesive";
  data: {
    id: string;
    title: string;
    content_type?: string;
    /** `"webpage"` marks a capture — sized like media, never a page. */
    origin_type?: string;
    width?: number;
    height?: number;
  };
}

export type FileRecordCanvasMetadata = {
  /** Authoritative server record for id + path (title derives from path leaf). */
  record: FileRecord;
  /** Client-measured natural width — used for initial node sizing when the
   *  server hasn't finished ingest yet. */
  naturalWidth?: number;
  /** Client-measured natural height. */
  naturalHeight?: number;
  /** MIME from the upload pipeline (post-HEIC). Falls back to record.content_type. */
  contentType?: string;
};

/**
 * Node-data display markers for a record whose display mode is "block" —
 * the stamp that routes the node to the live block card. Paint never reads
 * the record (it must route synchronously from room storage), so a
 * reference or copy placed from the library carries the record's display
 * facts onto the node here, exactly as the create tool stamps them: the
 * current model (`display`) plus the legacy `presentation` marker deployed
 * clients still read. Undefined for documents and legacy records.
 */
export function blockMarkersFromRecord(record: {
  display?: { mode?: string; chromeless?: boolean } | null;
}):
  | { display: Record<string, unknown>; presentation: Record<string, unknown> }
  | undefined {
  if (record.display?.mode !== "block") return undefined;
  const chromeless = record.display.chromeless === true;
  return {
    display: { mode: "block", ...(chromeless ? { chromeless: true } : {}) },
    presentation: {
      interactive: true,
      ...(chromeless ? { frameless: true, transparent: true } : {}),
    },
  };
}

/** The box a block record remembers (server-normalized to the lattice). */
export function blockRecordBox(record: {
  display?: { width?: number; height?: number } | null;
}): { width: number; height: number } | undefined {
  const { width, height } = record.display ?? {};
  return typeof width === "number" &&
    typeof height === "number" &&
    width > 0 &&
    height > 0
    ? { width, height }
    : undefined;
}

/**
 * Build a file_record canvas entity + initial node dimensions from upload
 * metadata. `entity.data` carries minimum-viable hints (id, title,
 * content_type, optional dims) so the canvas card renders the correct
 * aspect / icon before `useHydrateFileRecordEntity` finishes; hydration
 * spreads the full FileRecord on top later, but doesn't clobber these
 * fields because they don't exist at the wire's top level.
 */
export function buildFileRecordEntityForCanvas(
  meta: FileRecordCanvasMetadata,
): {
  entity: FileRecordCanvasEntity;
  nodeWidth: number;
  nodeHeight: number;
  /** Present when the record is a block — spread into the node's data. */
  blockMarkers?: ReturnType<typeof blockMarkersFromRecord>;
} {
  const { record, naturalWidth, naturalHeight, contentType } = meta;
  const ct = contentType || record.content_type || "";
  const title = record.path.split("/").pop() || record.id;

  // Prefer client-measured natural dims (upload path), fall back to the
  // server-stored dims on the record itself (copy/place-existing paths).
  const w = naturalWidth ?? record.width;
  const h = naturalHeight ?? record.height;

  const originType = record.origin_type;
  // Photos keep their aspect; so does a web-page capture, whose screenshot
  // poster carries the source dimensions.
  const fitted =
    (ct.startsWith("image/") || originType === WEBPAGE_ORIGIN_TYPE) &&
    w != null &&
    h != null &&
    w > 0 &&
    h > 0;
  const blockMarkers = blockMarkersFromRecord(record);
  const box = fitted
    ? fitImageNodeSize(w, h)
    : blockMarkers
      ? // A referenced block renders LIVE at the box the record remembers —
        // authored at create, refreshed at promote — falling back to the
        // landscape block tile for records from before the box was stored.
        (blockRecordBox(record) ?? {
          width: DEFAULT_FILE_NODE_WIDTH,
          height: DEFAULT_FILE_NODE_HEIGHT,
        })
      : // Pages land portrait. This builder serves uploads, workspace
        // copies and references, paste ghosts and URL ingest, so one branch
        // here keeps every route consistent.
        defaultFileRecordNodeBox(ct, originType);

  return {
    entity: {
      type: "file_record",
      base: "file_record",
      platform: "cohesive",
      data: {
        id: record.id,
        title,
        ...(ct ? { content_type: ct } : {}),
        ...(originType ? { origin_type: originType } : {}),
        ...(fitted ? { width: w, height: h } : {}),
        // Carry the tier on the node itself (D2): menus resolve the row from
        // the canvas list, but that list can lag a just-minted reference —
        // without this the node would briefly offer "Archive" on a reference,
        // the one thing D2 forbids.
        ...(record.kind ? { kind: record.kind } : {}),
        ...(record.target_file_record_id
          ? { target_file_record_id: record.target_file_record_id }
          : {}),
      },
    },
    nodeWidth: box.width,
    nodeHeight: box.height,
    blockMarkers,
  };
}

export { readImageFileDimensions } from "@/utils/imageFilePrep";

export type ImageNodeSize = { width: number; height: number };

/**
 * Fit a node box so the longer side equals maxEdge (default 320), preserving aspect ratio.
 * If the shorter side would be below minShortEdge (default 8), scale up uniformly so the
 * short side is minShortEdge, then if the long side exceeds maxEdge, scale down uniformly
 * to cap the long side at maxEdge (short side may end up slightly above minShortEdge).
 */
export function fitImageNodeSize(
  naturalW: number,
  naturalH: number,
  maxEdge = 320,
  minShortEdge = 8,
): ImageNodeSize {
  if (naturalW <= 0 || naturalH <= 0) {
    return { width: maxEdge, height: maxEdge };
  }
  const longSide = Math.max(naturalW, naturalH);
  let w = (naturalW / longSide) * maxEdge;
  let h = (naturalH / longSide) * maxEdge;
  const short = Math.min(w, h);
  if (short < minShortEdge && short > 0) {
    const bump = minShortEdge / short;
    w *= bump;
    h *= bump;
  }
  const longAfter = Math.max(w, h);
  if (longAfter > maxEdge && longAfter > 0) {
    const cap = maxEdge / longAfter;
    w *= cap;
    h *= cap;
  }
  return { width: w, height: h };
}

/**
 * Fitted node size for entities that carry pixel dimensions in `data`.
 * Covers: generated `base: "file"` with image mime and `base: "file_record"`.
 */
export function getEntityMediaDisplayBox(
  entity: unknown,
): ImageNodeSize | null {
  if (!entity || typeof entity !== "object") {
    return null;
  }
  const e = entity as {
    type?: string;
    base?: string;
    data?: Record<string, unknown>;
  };
  if (!e.data || typeof e.data !== "object") {
    return null;
  }
  const mw = e.data.width;
  const mh = e.data.height;
  if (typeof mw !== "number" || typeof mh !== "number" || mw <= 0 || mh <= 0) {
    return null;
  }
  if (e.base === "file") {
    const mime = String(e.data.mime_type ?? "");
    if (mime.startsWith("image/")) {
      return fitImageNodeSize(mw, mh);
    }
  }
  if (e.base === "file_record") {
    const mime = String(
      e.data.content_type ?? (e.data as { mime_type?: string }).mime_type ?? "",
    );
    if (fitsNaturalAspect(mime, e.data.origin_type)) {
      return fitImageNodeSize(mw, mh);
    }
  }
  return null;
}

/**
 * Same as `getEntityMediaDisplayBox` but also looks at the
 * `metadata.width/height` shape that hydrated `file_record` entities
 * inherit from the server `FileRecord`. The server only populates
 * `metadata.width/height` once the processing pipeline finishes, so this
 * is the post-processing path; the top-level `e.data.width/height` path
 * (used by canvas drops) is the pre-processing one.
 */
export function getFileRecordMediaDisplayBoxFromMetadata(
  entity: unknown,
): ImageNodeSize | null {
  if (!entity || typeof entity !== "object") return null;
  const e = entity as {
    base?: string;
    data?: Record<string, unknown> & {
      metadata?: { width?: unknown; height?: unknown };
    };
  };
  if (e.base !== "file_record" || !e.data) return null;
  const mime = String(
    e.data.content_type ?? (e.data as { mime_type?: string }).mime_type ?? "",
  );
  if (!fitsNaturalAspect(mime, e.data.origin_type)) return null;
  const mw = e.data.metadata?.width;
  const mh = e.data.metadata?.height;
  if (typeof mw !== "number" || typeof mh !== "number" || mw <= 0 || mh <= 0) {
    return null;
  }
  return fitImageNodeSize(mw, mh);
}
